import { useState } from 'react';
import { FileText, Download, LoaderCircle } from 'lucide-react';
import { useProject } from '@/lib/ProjectContext';
import { usePermissions } from '@/lib/usePermissions';
import { API_BASE } from '@/design/tokens';
import { supabase } from '@/lib/supabaseClient';

export function AuditReportPage() {
  const { activeProject } = useProject();
  const { canGenerateReport } = usePermissions();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [generating, setGenerating] = useState(false);
  const [lastHash, setLastHash] = useState<string | null>(null);
  const [error, setError] = useState('');

  const handleGenerate = async () => {
    setError('');
    setGenerating(true);
    setLastHash(null);

    try {
      const params = new URLSearchParams();
      if (activeProject) params.set('project_id', activeProject.id);
      if (startDate) params.set('start_date', new Date(startDate).toISOString());
      if (endDate) params.set('end_date', new Date(endDate).toISOString());

      const { data: { session } } = await supabase.auth.getSession();
      const headers: Record<string, string> = {};
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }

      const res = await fetch(`${API_BASE}/api/v1/reports/audit?${params}`, {
        method: 'POST',
        headers,
      });

      if (!res.ok) {
        const body = await res.text();
        throw new Error(`Failed to generate report: ${body}`);
      }

      const hash = res.headers.get('X-Report-Hash');
      setLastHash(hash);

      // Download the PDF
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = res.headers.get('Content-Disposition')?.match(/filename="(.+)"/)?.[1] || 'audit-report.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <p className="text-[10px] font-semibold text-brand-orange uppercase tracking-widest">
          Chrona Compliance
        </p>
        <h1 className="text-xl font-bold text-brand-charcoal">Audit Report</h1>
        <p className="text-sm text-slate-500 mt-1">
          Generate a PDF audit trail report for compliance documentation.
        </p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              Start Date (optional)
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange/30"
            />
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              End Date (optional)
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange/30"
            />
          </div>
        </div>

        <div className="bg-slate-50 rounded-lg p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Report Contents</p>
          <ul className="text-sm text-slate-600 space-y-1">
            <li>Audit event log with checksums</li>
            <li>Human override records with rationale</li>
            <li>Readiness snapshots with prompt versions</li>
            <li>Report integrity hash (SHA-256)</li>
          </ul>
        </div>

        {error && (
          <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}

        {lastHash && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
            <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wider mb-1">Report Generated</p>
            <p className="text-xs text-emerald-600 font-mono break-all">
              SHA-256: {lastHash}
            </p>
          </div>
        )}

        <div className="flex justify-end">
          <button
            onClick={handleGenerate}
            disabled={generating || !canGenerateReport}
            title={!canGenerateReport ? 'Requires Lead role' : undefined}
            className={`inline-flex items-center gap-2 px-5 py-2.5 bg-brand-orange text-white text-sm font-semibold rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50${!canGenerateReport ? ' cursor-not-allowed' : ''}`}
          >
            {generating ? (
              <LoaderCircle className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            Generate Audit Report
          </button>
        </div>
      </div>
    </div>
  );
}
